import { Tooltip, TooltipContent, TooltipTrigger } from '@deepguide-ai/ui/tooltip'
import { MemberAvatar } from '@/components/compliance/MemberAvatar'
import { cn } from '@/lib/utils'

export interface AvatarGroupUser {
  id: string
  name: string | null
  email?: string
  image: string | null
}

interface AvatarGroupProps {
  users: AvatarGroupUser[]
  /** Max avatars to show before +N overflow */
  max?: number
  /** Size variant */
  size?: 'sm' | 'md'
  /** Disable per-avatar tooltips when a parent surface provides one group tooltip. */
  showIndividualTooltips?: boolean
  className?: string
}

const sizeConfig = {
  sm: {
    avatar: 'h-5 w-5',
    overflow: 'h-5 w-5 text-[10px] leading-5',
    spacing: '-space-x-1.5',
  },
  md: {
    avatar: 'h-6 w-6',
    overflow: 'h-6 w-6 text-[10px]',
    spacing: '-space-x-1.5',
  },
}

export function AvatarGroup({
  users,
  max = 3,
  size = 'sm',
  showIndividualTooltips = true,
  className,
}: AvatarGroupProps) {
  if (users.length === 0) return null

  const config = sizeConfig[size]
  const displayName = (u: AvatarGroupUser) => u.name || u.email || '?'
  const hiddenUsers = users.slice(max)
  const overflowClassName = cn(
    'z-1 flex items-center justify-center rounded-full border-0 bg-muted text-center font-medium text-muted-foreground ring-2 ring-card',
    config.overflow
  )

  return (
    <div className={cn('flex items-center', config.spacing, className)}>
      {users.slice(0, max).map((u) => {
        const avatar = (
          <span className='flex items-center'>
            <MemberAvatar
              person={{ name: u.name, email: u.email, image: u.image }}
              size='sm'
              className={cn(config.avatar, 'border-0 ring-2 ring-card')}
            />
          </span>
        )

        return showIndividualTooltips ? (
          <Tooltip key={u.id}>
            <TooltipTrigger asChild>{avatar}</TooltipTrigger>
            <TooltipContent>{displayName(u)}</TooltipContent>
          </Tooltip>
        ) : (
          <span key={u.id} className='flex items-center'>
            {avatar}
          </span>
        )
      })}
      {hiddenUsers.length > 0 &&
        (showIndividualTooltips ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <span
                aria-label={`${hiddenUsers.length} additional users: ${hiddenUsers.map(displayName).join(', ')}`}
                className={overflowClassName}
              >
                +{hiddenUsers.length}
              </span>
            </TooltipTrigger>
            <TooltipContent>
              {hiddenUsers.map(displayName).join(', ')}
            </TooltipContent>
          </Tooltip>
        ) : (
          <span
            aria-label={`${hiddenUsers.length} additional users`}
            className={overflowClassName}
          >
            +{hiddenUsers.length}
          </span>
        ))}
    </div>
  )
}
